const form = {
  /**
   * Converts the form data from the given form element into a plain JavaScript object.
   *
   * @param {HTMLFormElement} $form - The form element whose data will be transformed into an object.
   * @return {Object} An object containing the key-value pairs of the form data.
   */
  toObject($form){
    return Object.fromEntries(new FormData($form));
  },

  /**
   * Validates the given form by checking the validity of each field.
   * If `reportValidity` is true, custom validity messages are set and displayed for invalid fields.
   *
   * @param {HTMLFormElement} $form - The form element to validate.
   * @param {boolean} reportValidity - Indicates whether to report validity errors to the user.
   * @return {boolean} Returns `true` if the form is valid, otherwise `false`.
   */
  isValid($form, reportValidity){
    const entries = Array.from($form.querySelectorAll('input, select, textarea')).map(e => [e.name, e.value]);
    return entries.every(([field, value]) => {
      const $field = $form[field];

      $field.setCustomValidity('');

      if(!$field.checkValidity()){
        if(reportValidity){
          const customValidityMessage = $field.getAttribute('title');
          if(customValidityMessage){
            $field.setCustomValidity(customValidityMessage);
          }
          $field.reportValidity();
        }
        return false;
      } else {
        return true;
      }
    });
  },

  /**
   * Saves the current state of a form to session storage.
   *
   * @param {Object} $form - The form object whose state needs to be saved.
   * @return {boolean} Returns true if the form state was successfully saved, otherwise false.
   */
  saveFormState($form) {
    if (!$form || !$form.id) return false;

    const formData = this.toObject($form);
    sessionStorage.setItem(`form__${$form.id}`, JSON.stringify(formData));
    return true;
  },

  /**
   * Loads the saved state of a form from the session storage and populates the form fields accordingly.
   * This method will also dispatch events when select elements are updated or when the form state is fully loaded.
   *
   * @param {HTMLFormElement} $form - The form element to populate with saved data.
   * @return {boolean} Returns true if the form state was successfully loaded, otherwise false.
   */
  loadFormState($form) {
    if (!$form || !$form.id) return false;

    const savedData = sessionStorage.getItem(`form__${$form.id}`);
    if (!savedData) return false;

    try {
      const formData = JSON.parse(savedData);
      let selectsChanged = [];

      // Populate form fields with saved values
      Object.entries(formData).forEach(([field, value]) => {
        const $field = $form.elements[field];
        if ($field) {
          if ($field.type === 'checkbox' || $field.type === 'radio') {
            $field.checked = value === 'on' || value === true;
          } else {
            $field.value = value;
          }

          // Track select elements that may need their output areas updated (i.e., for conditionally displaying data)
          if ($field.tagName === 'SELECT') {
            selectsChanged.push($field);
          }
        }
      });

      // Dispatch a custom event for each changed select
      selectsChanged.forEach($select => {
        // Create and dispatch a synthetic change event
        const changeEvent = new Event('change', { bubbles: true });
        $select.dispatchEvent(changeEvent);
      });

      // Dispatch a form-state-loaded event that other modules can listen for
      const formStateLoadedEvent = new CustomEvent('form-state-loaded', {
        bubbles: true,
        detail: { id: $form.id, formElement: $form }
      });
      $form.dispatchEvent(formStateLoadedEvent);

      return true;
    } catch (err) {
      console.error('Error loading form data from session storage', err);
      return false;
    }
  },

  /**
   * Resets and clears the state of the specified form by removing its associated data from session storage.
   *
   * @param {HTMLFormElement} $form - The form element whose state is to be cleared. The form must have a valid ID.
   * @return {boolean} Returns true if the form state was successfully cleared; otherwise, returns false.
   */
  clearFormState($form) {
    if (!$form || !$form.id) return false;

    try {
      sessionStorage.removeItem(`form__${$form.id}`);
      return true;
    } catch (err) {
      console.error('Error clearing form data from session storage', err);
      return false;
    }
  },

  /**
   * Monitors a specified form for changes in its input fields and saves their state.
   *
   * @param {HTMLFormElement} $form - The form element to watch for changes.
   * @return {boolean} Returns true if the form is successfully initialized for change detection, otherwise false.
   */
  watchFormForChanges($form) {
    if (!$form || !$form.id) return false;

    const handleChange = () => this.saveFormState($form);

    // Get all form inputs
    const inputs = $form.querySelectorAll('input, select, textarea');

    // Add event listeners to inputs
    inputs.forEach(input => {
      input.addEventListener('change', handleChange);
      input.addEventListener('input', handleChange);
    });

    // Try to load any previously saved data from the session
    this.loadFormState($form);

    return true;
  }
};

export default form;
